import { ComponentProps, FC, ReactNode } from 'react';
import { Accordion as RadixAccordion } from 'radix-ui';
import { twMerge } from 'tailwind-merge';
import { MatchOverview, TeamInfo } from '../../MatchCard';
import { CompetitionLogo } from '../../CompetitionLogo';
import { Typography } from '../../Typography';

export type FixtureCardSize = 'small' | 'large' | 'responsive';
export type FixtureCardState = 'fixture' | 'result';

export interface FixtureStatRow {
  label: string;
  total: string | number;
  points: string | number;
  icon?: ReactNode;
}

export interface FixtureOddsEntry {
  label: string;
  value: string;
  href?: string;
}

export interface FixtureOddsStripData {
  brand?: ReactNode;
  entries: FixtureOddsEntry[];
}

export interface FixtureCompetitionData {
  label: string;
  competitionCode?: string;
  icon?: ReactNode;
}

export interface FixtureCardProps extends Omit<ComponentProps<'article'>, 'children'> {
  id?: string;
  size?: FixtureCardSize;
  state?: FixtureCardState;
  competition: FixtureCompetitionData;
  venue?: string;
  leftTeam: TeamInfo;
  rightTeam: TeamInfo;
  kickOff?: string;
  status?: string;
  statusVariant?: 'finished' | 'live';
  detailsLabel?: string;
  onDetailsClick?: () => void;
  // When false the "Match Details" button is omitted entirely. Used to hide
  // the link for additional-league fixtures that have no Match Detail page.
  showDetails?: boolean;
  mdTotalLabel?: string;
  mdTotalPoints?: string;
  oddsStrip?: FixtureOddsStripData;
  stats?: FixtureStatRow[];
  viewDetailsLabel?: string;
  onViewDetailsClick?: () => void;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export interface FixtureCardStackSection {
  id?: string;
  title: string;
  cards: Omit<FixtureCardProps, 'size'>[];
}

export interface FixtureCardStackProps extends Omit<ComponentProps<'div'>, 'children'> {
  size?: FixtureCardSize;
  sections: FixtureCardStackSection[];
}

const ITEM_VALUE = 'fixture-card-item';

const sizeClassMap = {
  stackPadding: {
    small: 'px-[16px] py-[24px]',
    large: 'px-0 py-0',
    responsive: 'px-[16px] py-[24px] md:px-0 md:py-0',
  },
  stackGap: {
    small: 'gap-[24px]',
    large: 'gap-[32px]',
    responsive: 'gap-[24px] md:gap-[32px]',
  },
  sectionHeading: {
    small: 'text-16 leading-20',
    large: 'text-20 leading-24',
    responsive: 'text-16 leading-20 md:text-20 md:leading-24',
  },
  cardTopGap: {
    small: 'gap-[12px]',
    large: 'gap-[16px]',
    responsive: 'gap-[12px] md:gap-[16px]',
  },
  venueText: {
    small: 'text-10 leading-10',
    large: 'text-12 leading-16',
    responsive: 'text-10 leading-10 md:text-12 md:leading-16',
  },
  competitionText: {
    small: 'text-10 leading-10',
    large: 'text-12 leading-16',
    responsive: 'text-10 leading-10 md:text-12 md:leading-16',
  },
  overviewPadding: {
    small: 'py-[12px]',
    large: 'py-[16px]',
    responsive: 'py-[12px] md:py-[16px]',
  },
  resultMetaText: {
    small: 'text-10 leading-10',
    large: 'text-12 leading-16',
    responsive: 'text-10 leading-10 md:text-12 md:leading-16',
  },
  chevronSize: {
    small: 'size-[20px]',
    large: 'size-[24px]',
    responsive: 'size-[20px] md:size-[24px]',
  },
} as const;

const CompetitionPill: FC<{
  size: FixtureCardSize;
  data: FixtureCompetitionData;
}> = ({ size, data }) => {
  const renderedIcon =
    data.icon ??
    (data.competitionCode ? (
      <CompetitionLogo competition={data.competitionCode} className="size-[16px]" />
    ) : null);

  return (
    <div className="inline-flex items-center gap-[4px] rounded-[4px] bg-[#eae9fd] px-[8px] py-[4px]">
      {renderedIcon}
      <Typography
        as="span"
        variant="paragraph000"
        className={twMerge('truncate text-[#222526]', sizeClassMap.competitionText[size])}
      >
        {data.label}
      </Typography>
    </div>
  );
};

const OddsStrip: FC<{
  size: FixtureCardSize;
  data: FixtureOddsStripData;
}> = ({ size, data }) => {
  if (!data.entries.length) return null;

  return null;

  const brand = data.brand ?? (
    <span className="text-[20px] leading-none font-black text-[#46ff33]">M</span>
  );

  const brandClassName =
    size === 'small'
      ? 'flex h-full shrink-0 items-center border-r border-[#2c2e30] px-[20px]'
      : size === 'large'
        ? 'flex h-full min-w-0 flex-1 items-center justify-center border-r border-[#2c2e30] px-[20px]'
        : 'flex h-full shrink-0 items-center border-r border-[#2c2e30] px-[20px] md:min-w-0 md:flex-1 md:justify-center';

  const oddsTypographyClassName =
    size === 'small'
      ? 'text-xs leading-12'
      : size === 'large'
        ? 'text-12 leading-16'
        : 'text-8 leading-12 md:text-12 md:leading-16';

  return (
    <div className="flex h-[32px] w-full items-center bg-[#161718]">
      <div className={brandClassName}>{brand}</div>
      {data.entries.map((entry, index) => {
        const EntryTag = entry.href ? 'a' : 'div';
        return (
          <EntryTag
            key={`${entry.label}-${index}`}
            {...(entry.href
              ? { href: entry.href, target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            className={twMerge(
              'flex h-full min-w-0 flex-1 items-center justify-center gap-[10px]',
              index === data.entries.length - 1 ? '' : 'border-r border-[#2c2e30]'
            )}
          >
            <Typography
              as="span"
              variant="paragraph000"
              className={twMerge(oddsTypographyClassName, 'text-[white]')}
            >
              {entry.label}
            </Typography>
            <div className="inline-flex items-center justify-center rounded-[4px] bg-[#2c2e30] px-[8px] py-[4px]">
              <Typography as="span" className={twMerge(oddsTypographyClassName, 'text-[#46ff33]')}>
                {entry.value}
              </Typography>
            </div>
          </EntryTag>
        );
      })}
    </div>
  );
};

const FixtureStatsTable: FC<{
  rows: FixtureStatRow[];
  onViewDetailsClick?: () => void;
  viewDetailsLabel?: string;
}> = ({ rows, onViewDetailsClick, viewDetailsLabel = 'View Match Details' }) => {
  if (!rows.length && !viewDetailsLabel) return null;

  return (
    <div className="w-full">
      {!!rows.length && (
        <>
          <div className="flex w-full items-center gap-[16px] border-b border-[#eae9fd] pb-[12px]">
            <Typography as="span" variant="paragraph000" className="min-w-0 flex-1 text-[#757777]">
              STAT
            </Typography>
            <Typography
              as="span"
              variant="paragraph000"
              className="w-[40px] shrink-0 text-center text-[#757777]"
            >
              TOTAL
            </Typography>
            <Typography
              as="span"
              variant="paragraph000"
              className="w-[40px] shrink-0 text-center text-[#757777]"
            >
              POINTS
            </Typography>
          </div>

          {rows.map((row, index) => (
            <div
              key={`${row.label}-${index}`}
              className="flex w-full items-center gap-[16px] border-b border-[#eae9fd] py-[12px]"
            >
              <div className="flex min-w-0 flex-1 items-center gap-[8px]">
                <Typography as="span" variant="subheadline010" className="truncate text-[#222526]">
                  {row.label}
                </Typography>
                {row.icon}
              </div>
              <Typography
                as="span"
                variant="subheadline020"
                className="w-[40px] shrink-0 text-center text-[#222526]"
              >
                {row.total}
              </Typography>
              <Typography
                as="span"
                variant="subheadline020"
                className="w-[40px] shrink-0 text-center text-[#0072ee]"
              >
                {row.points}
              </Typography>
            </div>
          ))}
        </>
      )}

      {viewDetailsLabel && (
        <button
          type="button"
          onClick={onViewDetailsClick}
          className="mx-auto mt-[12px] inline-flex h-[26px] items-center justify-center px-[8px] py-[8px]"
        >
          <Typography as="span" variant="button020" className="text-[#0072ee]">
            {viewDetailsLabel}
          </Typography>
        </button>
      )}
    </div>
  );
};

export const FixtureCard: FC<FixtureCardProps> = ({
  className,
  size = 'responsive',
  state = 'fixture',
  competition,
  venue,
  leftTeam,
  rightTeam,
  kickOff,
  status = 'FT',
  statusVariant = 'finished',
  detailsLabel = 'Match Details',
  onDetailsClick,
  showDetails = true,
  mdTotalLabel = 'MD 1 Total:',
  mdTotalPoints = '11 PTS',
  oddsStrip,
  stats = [],
  viewDetailsLabel = 'View Match Details',
  onViewDetailsClick,
  open,
  defaultOpen = false,
  onOpenChange,
  ...otherProps
}) => {
  const isFixture = state === 'fixture';
  const isResult = state === 'result';
  const isControlled = typeof open === 'boolean';
  const accordionValue = open ? ITEM_VALUE : '';
  // const hasExpandableContent = isResult && (stats.length > 0 || Boolean(viewDetailsLabel));
  const hasExpandableContent = false;
  const topVenueClass =
    size === 'small' ? 'hidden' : size === 'large' ? 'block' : 'hidden md:block';

  const overviewVenue =
    isFixture && venue && (size === 'small' || size === 'responsive') ? venue : undefined;

  const overviewClassName = twMerge(
    'w-full rounded-[4px] bg-[#eae9fd]',
    sizeClassMap.overviewPadding[size],
    size === 'responsive' && isFixture ? '[&>p]:md:hidden' : ''
  );

  const overview = (
    <MatchOverview
      leftTeam={leftTeam}
      rightTeam={rightTeam}
      kickOff={isFixture ? kickOff : undefined}
      status={isResult ? status : undefined}
      statusVariant={isResult ? statusVariant : undefined}
      venue={overviewVenue}
      className={overviewClassName}
    />
  );

  const headerLeft = (
    <div className="flex min-w-0 flex-1 items-center gap-[12px]">
      <CompetitionPill size={size} data={competition} />
      {venue && (
        <Typography
          as="span"
          variant="paragraph000"
          className={twMerge(
            'min-w-0 truncate whitespace-nowrap text-[#222526]',
            sizeClassMap.venueText[size],
            topVenueClass
          )}
        >
          {venue}
        </Typography>
      )}
    </div>
  );

  if (isFixture) {
    return (
      <article
        className={twMerge(
          'flex w-full flex-col gap-[12px] rounded-[12px] bg-[white] p-[12px] shadow-[0px_4px_24px_0px_rgba(34,37,38,0.2)]',
          className
        )}
        {...otherProps}
      >
        <div className="flex w-full items-center justify-between gap-[16px]">
          {headerLeft}
          {showDetails && (
            <button
              type="button"
              onClick={onDetailsClick}
              className="inline-flex h-[26px] shrink-0 items-center justify-center px-[8px] py-[8px]"
            >
              <Typography as="span" variant="button020" className="text-[#0072ee]">
                {detailsLabel}
              </Typography>
            </button>
          )}
        </div>

        <div className="w-full overflow-hidden rounded-[4px]">
          {overview}
          {oddsStrip && <OddsStrip size={size} data={oddsStrip} />}
        </div>
      </article>
    );
  }

  // const trigger = (
  //   <button
  //     type="button"
  //     className="group inline-flex shrink-0 items-center gap-[8px] p-0 text-left"
  //     aria-label="Toggle match stats"
  //   >
  //     <Typography
  //       as="span"
  //       variant="paragraph000"
  //       className={twMerge('text-[#222526]', sizeClassMap.resultMetaText[size])}
  //     >
  //       {mdTotalLabel}
  //     </Typography>
  //     <span className="inline-flex items-center justify-center rounded-[4px] bg-[#0072ee] px-[8px] py-[2px]">
  //       <Typography as="span" variant="subheadline020" className="text-[white]">
  //         {mdTotalPoints}
  //       </Typography>
  //     </span>
  //     <ChevronUp
  //       className={twMerge(
  //         'rotate-180 text-[#222526] transition-transform duration-200 ease-out group-data-[state=open]:rotate-0',
  //         sizeClassMap.chevronSize[size]
  //       )}
  //       aria-hidden
  //     />
  //   </button>
  // );

  return (
    <RadixAccordion.Root
      type="single"
      collapsible
      value={isControlled ? accordionValue : undefined}
      defaultValue={isControlled ? undefined : defaultOpen ? ITEM_VALUE : undefined}
      onValueChange={value => onOpenChange?.(value === ITEM_VALUE)}
      className="w-full"
    >
      <RadixAccordion.AccordionItem value={ITEM_VALUE} className="w-full border-none">
        <article
          className={twMerge(
            'flex w-full flex-col gap-[12px] rounded-[12px] bg-[white] p-[12px] shadow-[0px_4px_24px_0px_rgba(34,37,38,0.2)]',
            className
          )}
          {...otherProps}
        >
          {/* <div className="flex w-full items-center justify-between gap-[16px]">
            {headerLeft}
            {hasExpandableContent ? (
              <RadixAccordion.AccordionTrigger asChild>{trigger}</RadixAccordion.AccordionTrigger>
            ) : (
              trigger
            )}
          </div> */}

          <div className="flex w-full items-center justify-between gap-[16px]">
            {headerLeft}
            {showDetails && (
              <button
                type="button"
                onClick={onDetailsClick}
                className="inline-flex h-[26px] shrink-0 items-center justify-center px-[8px] py-[8px]"
              >
                <Typography as="span" variant="button020" className="text-[#0072ee]">
                  {detailsLabel}
                </Typography>
              </button>
            )}
          </div>

          {/* The result card covers the whole in-play period, so the strip has
              to live here too — not just on the pre-match card. */}
          <div className="w-full overflow-hidden rounded-[4px]">
            {overview}
            {oddsStrip && <OddsStrip size={size} data={oddsStrip} />}
          </div>

          {hasExpandableContent && (
            <RadixAccordion.AccordionContent className="overflow-hidden data-[state=closed]:animate-slideUp data-[state=open]:animate-slideDown">
              <FixtureStatsTable
                rows={stats}
                onViewDetailsClick={onViewDetailsClick}
                viewDetailsLabel={viewDetailsLabel}
              />
            </RadixAccordion.AccordionContent>
          )}
        </article>
      </RadixAccordion.AccordionItem>
    </RadixAccordion.Root>
  );
};

export const FixtureCardStack: FC<FixtureCardStackProps> = ({
  className,
  size = 'responsive',
  sections,
  ...otherProps
}) => {
  return (
    <div
      className={twMerge(
        'flex w-full flex-col',
        sizeClassMap.stackPadding[size],
        sizeClassMap.stackGap[size],
        className
      )}
      {...otherProps}
    >
      {sections.map((section, sectionIndex) => (
        <section
          key={section.id ?? `${section.title}-${sectionIndex}`}
          className="flex w-full flex-col gap-[20px]"
        >
          <Typography
            as="h3"
            variant="subheadline030"
            className={twMerge('text-[#222526]', sizeClassMap.sectionHeading[size])}
          >
            {section.title}
          </Typography>

          <div className="flex w-full flex-col gap-[12px]">
            {section.cards.map((card, cardIndex) => (
              <FixtureCard
                key={card.id ?? `${section.title}-card-${cardIndex}`}
                size={size}
                {...card}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
